import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Calculator, Flame, Pill, Info, Sparkles, Save, Droplets, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_LEVELS = [
  { value: 1.2, label: 'Sedentary', desc: 'Little or no exercise' },
  { value: 1.375, label: 'Light', desc: '1–3 days/week of light exercise' },
  { value: 1.55, label: 'Moderate', desc: '3–5 days/week' },
  { value: 1.725, label: 'Active', desc: '6–7 days/week' },
  { value: 1.9, label: 'Very Active', desc: 'Hard daily exercise + physical job' },
];

const GOALS = [
  { key: 'lose2', label: 'Lose 2 lbs/week', delta: -1000 },
  { key: 'lose1', label: 'Lose 1 lb/week', delta: -500 },
  { key: 'lose05', label: 'Lose 0.5 lb/week', delta: -250 },
  { key: 'maintain', label: 'Maintain Weight', delta: 0 },
  { key: 'gain05', label: 'Gain 0.5 lb/week', delta: 250 },
  { key: 'gain1', label: 'Gain 1 lb/week', delta: 500 },
];

// Mifflin-St Jeor + age-adjusted (-5% for 50+ to better match senior metabolism)
const calculateBMR = (gender, weightKg, heightCm, age) => {
  const base = gender === 'male'
    ? 10 * weightKg + 6.25 * heightCm - 5 * age + 5
    : 10 * weightKg + 6.25 * heightCm - 5 * age - 161;
  return age >= 50 ? Math.round(base * 0.95) : Math.round(base);
};

const goalToSupplements = {
  lose2: { tag: 'fat-loss', label: 'Fat-loss support', supps: ['Berberine', 'Green Tea Extract', 'L-Carnitine', 'Fiber'] },
  lose1: { tag: 'fat-loss', label: 'Fat-loss support', supps: ['Berberine', 'Green Tea Extract', 'Fiber'] },
  lose05: { tag: 'metabolic', label: 'Metabolic balance', supps: ['Chromium Picolinate', 'Fiber', 'Probiotics'] },
  maintain: { tag: 'longevity', label: 'Longevity stack', supps: ['Vitamin D3', 'Magnesium', 'Omega-3', 'CoQ10'] },
  gain05: { tag: 'muscle', label: 'Muscle building', supps: ['Whey Protein', 'Creatine', 'BCAAs'] },
  gain1: { tag: 'muscle', label: 'Muscle building', supps: ['Whey Protein', 'Creatine', 'BCAAs', 'Vitamin D3'] },
};

// Per-supplement dose formulas (mg per kg of bodyweight unless noted)
const DOSE_SUPPLEMENTS = [
  { name: 'Vitamin D3', formula: (kg) => `${Math.round(kg * 40)} IU/day`, note: '40 IU per kg, max 4000 IU' },
  { name: 'Creatine Monohydrate', formula: (kg) => `${(kg * 0.1).toFixed(1)} g/day`, note: '0.1 g per kg (maintenance)' },
  { name: 'Magnesium (Glycinate)', formula: (kg) => `${Math.round(kg * 5)} mg/day`, note: '5 mg per kg, divided doses' },
  { name: 'Protein (daily target)', formula: (kg) => `${Math.round(kg * 1.6)} g/day`, note: '1.6 g per kg for active adults' },
  { name: 'Omega-3 (EPA+DHA)', formula: () => `1000–2000 mg/day`, note: 'Fixed range, not weight-dependent' },
  { name: 'Vitamin C', formula: (kg) => `${Math.min(Math.round(kg * 10), 1000)} mg/day`, note: '10 mg per kg, capped at 1000 mg' },
  { name: 'Zinc', formula: () => `15–30 mg/day`, note: 'Fixed range; do not exceed 40 mg' },
  { name: 'B12 (Methylcobalamin)', formula: () => `500–1000 mcg/day`, note: 'Higher for 50+ due to absorption decline' },
];

export default function CalculatorPage() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('calories');
  const [units, setUnits] = useState('us');

  // Form state
  const [age, setAge] = useState('40');
  const [gender, setGender] = useState('male');
  const [heightFt, setHeightFt] = useState('5');
  const [heightIn, setHeightIn] = useState('9');
  const [heightCm, setHeightCm] = useState('175');
  const [weightLbs, setWeightLbs] = useState('170');
  const [weightKg, setWeightKg] = useState('77');
  const [activity, setActivity] = useState(1.55);
  const [selectedGoal, setSelectedGoal] = useState('maintain');
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [creatingHydration, setCreatingHydration] = useState(false);
  const [hydrationCreated, setHydrationCreated] = useState(false);
  // Track which goal-suggested supplements are already in user's stack
  const [stackAdded, setStackAdded] = useState({}); // { 'Berberine': true }
  const [stackAdding, setStackAdding] = useState(null); // currently-being-added name

  // On mount: read existing reminders so we can mark goal supps that are already in the stack
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/reminders`, { headers: getAuthHeader() })
      .then(res => {
        const have = {};
        (res.data || []).forEach(r => {
          const n = (r.supplement_name || '').toLowerCase().trim();
          if (n) have[n] = true;
        });
        // Map back any goal-suggested supps that match
        const matched = {};
        Object.values(goalToSupplements).forEach(g => {
          g.supps.forEach(name => {
            if (have[name.toLowerCase()]) matched[name] = true;
          });
        });
        setStackAdded(matched);
      })
      .catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const addSuppToStack = async (supplementName) => {
    if (!user || stackAdded[supplementName] || stackAdding) return;
    setStackAdding(supplementName);
    try {
      await axios.post(
        `${API}/reminders`,
        {
          supplement_name: supplementName,
          time: '08:00',
          days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
          enabled: true,
          is_medication: false,
        },
        { headers: getAuthHeader() }
      );
      setStackAdded(prev => ({ ...prev, [supplementName]: true }));
      toast.success(`${supplementName} added to your stack — daily 08:00 reminder set`);
    } catch (e) {
      console.error('Add to stack failed:', e);
      toast.error('Could not add to stack. Please try again.');
    } finally {
      setStackAdding(null);
    }
  };

  // Prefill from quiz if logged in
  useEffect(() => {
    if (!user) return;
    const headers = getAuthHeader();
    fetch(`${API}/assessment/latest-answers`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.answers) return;
        const ans = data.answers;
        if (ans.age) setAge(String(ans.age));
        if (ans.gender) setGender(String(ans.gender).toLowerCase());
        if (ans.weight_lbs) { setWeightLbs(String(ans.weight_lbs)); setWeightKg(String(Math.round(ans.weight_lbs * 0.4536))); }
        if (ans.weight_kg) { setWeightKg(String(ans.weight_kg)); setWeightLbs(String(Math.round(ans.weight_kg / 0.4536))); }
        if (ans.height_in) {
          const inches = parseInt(ans.height_in);
          setHeightFt(String(Math.floor(inches / 12)));
          setHeightIn(String(inches % 12));
          setHeightCm(String(Math.round(inches * 2.54)));
        }
      })
      .catch(() => {});
  }, [user]);

  // Convert to metric for calculation
  const wKg = units === 'us'
    ? Math.round(parseFloat(weightLbs || 0) * 0.4536)
    : parseFloat(weightKg || 0);
  const hCm = units === 'us'
    ? Math.round((parseInt(heightFt || 0) * 12 + parseInt(heightIn || 0)) * 2.54)
    : parseFloat(heightCm || 0);
  const ageNum = parseInt(age || 0);

  const bmr = useMemo(() => {
    if (!wKg || !hCm || !ageNum) return 0;
    return calculateBMR(gender, wKg, hCm, ageNum);
  }, [gender, wKg, hCm, ageNum]);

  const tdee = useMemo(() => Math.round(bmr * activity), [bmr, activity]);
  const selectedCals = useMemo(() => {
    const g = GOALS.find(x => x.key === selectedGoal);
    return Math.max(1200, tdee + (g?.delta || 0));
  }, [tdee, selectedGoal]);

  // Macros for selected goal
  const macros = useMemo(() => {
    if (!selectedCals || !wKg) return null;
    const proteinG = Math.round(wKg * 2.2); // 1g per lb bodyweight
    const fatG = Math.round((selectedCals * 0.25) / 9);
    const carbsG = Math.max(0, Math.round((selectedCals - proteinG * 4 - fatG * 9) / 4));
    const fiberG = Math.round((selectedCals / 1000) * (ageNum >= 50 ? 16 : 14));
    return { proteinG, fatG, carbsG, fiberG };
  }, [selectedCals, wKg, ageNum]);

  const suppRec = goalToSupplements[selectedGoal];

  const handleSave = async () => {
    if (!user) {
      toast.info('Sign in to save your calculation');
      navigate('/auth');
      return;
    }
    if (!macros) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/calculator/save`,
        {
          bmr, tdee,
          selected_goal: selectedGoal,
          selected_calories: selectedCals,
          protein_g: macros.proteinG,
          carbs_g: macros.carbsG,
          fat_g: macros.fatG,
          fiber_g: macros.fiberG,
          weight_kg: wKg,
          age: ageNum,
          activity_level: activity,
        },
        { headers: getAuthHeader() }
      );
      setSaveResult(data);
      toast.success('Calculation saved');
    } catch (e) {
      toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  const handleHydrationSetup = async () => {
    if (!user) {
      toast.info('Sign in to add hydration reminders');
      navigate('/auth');
      return;
    }
    setCreatingHydration(true);
    try {
      const cups = Math.max(6, Math.min(12, Math.round(selectedCals / 280))); // ~1 cup per 280 cal
      const { data } = await axios.post(
        `${API}/calculator/hydration-reminders`,
        { cups_per_day: cups, start_time: "08:00", end_time: "20:00" },
        { headers: getAuthHeader() }
      );
      setHydrationCreated(true);
      toast.success(`${data.created} water reminders added (every ~2 hours)`);
    } catch (e) {
      toast.error('Could not add hydration reminders');
    } finally {
      setCreatingHydration(false);
    }
  };

  const formatDelta = (n, unit = '') => {
    if (n === 0) return <span className="text-muted-foreground">no change</span>;
    const Icon = n > 0 ? TrendingUp : TrendingDown;
    const color = n > 0 ? 'text-amber-700' : 'text-emerald-700';
    return <span className={`inline-flex items-center gap-0.5 ${color} font-medium`}><Icon className="w-3 h-3" />{n > 0 ? '+' : ''}{n}{unit}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/30 to-white pb-24">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary" />
            Health Calculators
          </h1>
        </div>

        {/* Tabs */}
        <div className="max-w-3xl mx-auto px-4 pb-3 flex gap-2">
          <button
            onClick={() => setActiveTab('calories')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 ${
              activeTab === 'calories' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
            }`}
            data-testid="tab-calories"
          >
            <Flame className="w-4 h-4" /> Calorie & Macros
          </button>
          <button
            onClick={() => setActiveTab('dose')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 ${
              activeTab === 'dose' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
            }`}
            data-testid="tab-dose"
          >
            <Pill className="w-4 h-4" /> Supplement Doses
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-4 space-y-5">
        {activeTab === 'calories' ? (
          <>
            {/* Units toggle */}
            <div className="flex bg-emerald-100 rounded-full p-1 max-w-xs mx-auto">
              <button onClick={() => setUnits('us')} className={`flex-1 py-1.5 text-xs font-medium rounded-full ${units === 'us' ? 'bg-white text-primary' : 'text-muted-foreground'}`}>US Units</button>
              <button onClick={() => setUnits('metric')} className={`flex-1 py-1.5 text-xs font-medium rounded-full ${units === 'metric' ? 'bg-white text-primary' : 'text-muted-foreground'}`}>Metric</button>
            </div>

            {/* Inputs */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-border p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Age</label>
                  <input type="number" value={age} onChange={(e) => setAge(e.target.value)} min="15" max="90" className="mt-1 w-full px-3 py-2 border border-border rounded-lg" data-testid="input-age" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Gender</label>
                  <div className="mt-1 flex gap-2">
                    <button onClick={() => setGender('male')} className={`flex-1 py-2 text-sm rounded-lg border ${gender === 'male' ? 'bg-primary text-white border-primary' : 'border-border'}`}>Male</button>
                    <button onClick={() => setGender('female')} className={`flex-1 py-2 text-sm rounded-lg border ${gender === 'female' ? 'bg-primary text-white border-primary' : 'border-border'}`}>Female</button>
                  </div>
                </div>
              </div>

              {units === 'us' ? (
                <>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Height</label>
                    <div className="flex gap-2 mt-1 items-center">
                      <input
                        type="number"
                        value={heightFt}
                        onChange={(e) => setHeightFt(e.target.value)}
                        placeholder="ft"
                        className="min-w-0 flex-1 px-3 py-2 border border-border rounded-lg"
                        data-testid="input-height-ft"
                      />
                      <span className="text-xs text-muted-foreground flex-shrink-0">ft</span>
                      <input
                        type="number"
                        value={heightIn}
                        onChange={(e) => setHeightIn(e.target.value)}
                        placeholder="in"
                        className="min-w-0 flex-1 px-3 py-2 border border-border rounded-lg"
                        data-testid="input-height-in"
                      />
                      <span className="text-xs text-muted-foreground flex-shrink-0">in</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Weight</label>
                    <div className="flex gap-2 mt-1">
                      <input type="number" value={weightLbs} onChange={(e) => setWeightLbs(e.target.value)} className="flex-1 px-3 py-2 border border-border rounded-lg" data-testid="input-weight" />
                      <span className="self-center text-xs text-muted-foreground">lbs</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Height (cm)</label>
                    <input type="number" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} className="mt-1 w-full px-3 py-2 border border-border rounded-lg" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Weight (kg)</label>
                    <input type="number" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} className="mt-1 w-full px-3 py-2 border border-border rounded-lg" />
                  </div>
                </>
              )}

              <div>
                <label className="text-xs font-medium text-muted-foreground">Activity Level</label>
                <select value={activity} onChange={(e) => setActivity(parseFloat(e.target.value))} className="mt-1 w-full px-3 py-2 border border-border rounded-lg" data-testid="select-activity">
                  {ACTIVITY_LEVELS.map(a => (
                    <option key={a.value} value={a.value}>{a.label} — {a.desc}</option>
                  ))}
                </select>
              </div>
            </motion.div>

            {/* Results */}
            {bmr > 0 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="results">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
                    <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">BMR (At rest)</p>
                    <p className="text-3xl font-bold text-emerald-900 mt-1">{bmr.toLocaleString()}</p>
                    <p className="text-[10px] text-emerald-700">cal/day</p>
                  </div>
                  <div className="bg-primary/10 rounded-2xl p-4 border border-primary/20">
                    <p className="text-[10px] uppercase tracking-wide text-primary font-semibold">Maintenance (TDEE)</p>
                    <p className="text-3xl font-bold text-primary mt-1">{tdee.toLocaleString()}</p>
                    <p className="text-[10px] text-primary/70">cal/day</p>
                  </div>
                </div>

                {/* Goal selector */}
                <div className="bg-white rounded-2xl border border-border p-5">
                  <h3 className="font-semibold text-sm mb-3">Calorie Goals by Target</h3>
                  <div className="space-y-1">
                    {GOALS.map(g => {
                      const cals = Math.max(1200, tdee + g.delta);
                      const selected = selectedGoal === g.key;
                      return (
                        <button
                          key={g.key}
                          onClick={() => setSelectedGoal(g.key)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all ${
                            selected ? 'bg-primary/10 border-2 border-primary' : 'hover:bg-muted/50 border-2 border-transparent'
                          }`}
                          data-testid={`goal-${g.key}`}
                        >
                          <span className={`text-sm ${selected ? 'font-semibold text-primary' : ''}`}>{g.label}</span>
                          <span className={`text-sm font-bold ${selected ? 'text-primary' : 'text-foreground'}`}>{cals.toLocaleString()} <span className="text-[10px] text-muted-foreground font-normal">cal/day</span></span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Macros */}
                {macros && (
                  <div className="bg-white rounded-2xl border border-border p-5">
                    <h3 className="font-semibold text-sm">Daily Macros &amp; Fiber</h3>
                    <p className="text-xs text-muted-foreground mb-3">Based on your <span className="font-medium">{GOALS.find(g => g.key === selectedGoal)?.label}</span> target of {selectedCals.toLocaleString()} cal/day.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                        <p className="text-[10px] uppercase font-semibold text-blue-700">Protein</p>
                        <p className="text-2xl font-bold text-blue-900">{macros.proteinG}g</p>
                        <p className="text-[10px] text-blue-700/70">~1g per lb bodyweight</p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
                        <p className="text-[10px] uppercase font-semibold text-amber-700">Carbs</p>
                        <p className="text-2xl font-bold text-amber-900">{macros.carbsG}g</p>
                        <p className="text-[10px] text-amber-700/70">After protein &amp; fat</p>
                      </div>
                      <div className="bg-rose-50 rounded-xl p-3 border border-rose-100">
                        <p className="text-[10px] uppercase font-semibold text-rose-700">Fat</p>
                        <p className="text-2xl font-bold text-rose-900">{macros.fatG}g</p>
                        <p className="text-[10px] text-rose-700/70">25% of total calories</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-3 border border-green-100">
                        <p className="text-[10px] uppercase font-semibold text-green-700">Fiber</p>
                        <p className="text-2xl font-bold text-green-900">{macros.fiberG}g</p>
                        <p className="text-[10px] text-green-700/70">{ageNum >= 50 ? '16g per 1000 cal (50+)' : '14g per 1000 cal'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Save + Hydration actions */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={handleSave}
                    disabled={saving || !macros}
                    variant="outline"
                    className="h-auto py-3 flex-col gap-1 border-primary/30 text-primary hover:bg-primary/5"
                    data-testid="save-calc-btn"
                  >
                    <Save className="w-4 h-4" />
                    <span className="text-xs font-semibold">{saving ? 'Saving...' : 'Save Calculation'}</span>
                  </Button>
                  <Button
                    onClick={handleHydrationSetup}
                    disabled={creatingHydration || hydrationCreated}
                    variant="outline"
                    className="h-auto py-3 flex-col gap-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="hydration-btn"
                  >
                    <Droplets className="w-4 h-4" />
                    <span className="text-xs font-semibold">{hydrationCreated ? 'Reminders Added ✓' : creatingHydration ? 'Adding...' : 'Add Water Reminders'}</span>
                  </Button>
                </div>

                {/* Delta vs last calculation */}
                {saveResult?.delta && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-xs space-y-1" data-testid="calc-delta">
                    <p className="font-semibold text-emerald-900">Since your last calculation</p>
                    <p className="text-emerald-800">
                      TDEE: {formatDelta(saveResult.delta.tdee_delta, ' cal')}
                      {' · '}
                      Weight: {formatDelta(saveResult.delta.weight_kg_delta, ' kg')}
                      {' · '}
                      Goal target: {formatDelta(saveResult.delta.selected_calories_delta, ' cal')}
                    </p>
                  </div>
                )}

                {/* Supplement cross-sell */}
                {suppRec && (
                  <div className="bg-gradient-to-br from-primary/5 to-emerald-50 rounded-2xl border border-primary/20 p-5">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm">Supplements that fit this goal</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{suppRec.label} — based on {GOALS.find(g => g.key === selectedGoal)?.label}</p>
                        {user && (
                          <p className="text-[11px] text-muted-foreground mt-1.5 italic">Tap to add to your stack — sets a daily 08:00 reminder.</p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {suppRec.supps.map(s => {
                            const added = stackAdded[s];
                            const adding = stackAdding === s;
                            const disabled = added || adding || !user;
                            return (
                              <button
                                key={s}
                                type="button"
                                onClick={() => user && addSuppToStack(s)}
                                disabled={disabled}
                                className={`text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5 transition-all ${
                                  added
                                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-200 cursor-default'
                                    : adding
                                      ? 'bg-primary/10 text-primary border border-primary/30 cursor-wait'
                                      : user
                                        ? 'bg-white text-primary border border-primary/30 hover:bg-primary hover:text-white hover:border-primary cursor-pointer'
                                        : 'bg-white text-primary/70 border border-primary/20 cursor-default'
                                }`}
                                data-testid={`goal-supp-add-${s.replace(/\s+/g, '-').toLowerCase()}`}
                              >
                                {added ? <>✓ {s} added</> : adding ? <>Adding…</> : <><span className="text-sm leading-none">+</span> {s}</>}
                              </button>
                            );
                          })}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => navigate(user ? '/my-plan' : '/quiz')}
                          className="mt-4 btn-primary"
                          data-testid="goal-supplement-cta"
                        >
                          {user ? 'View my plan' : 'Get my personalized stack'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Disclaimer */}
                <div className="bg-muted/30 rounded-xl p-3 text-[11px] text-muted-foreground flex gap-2">
                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <p>Estimates based on the Mifflin-St Jeor equation{ageNum >= 50 ? ', adjusted for age 50+' : ''}. Individual results vary. Do not reduce intake by more than 1,000 cal/day. Consult a healthcare provider before significant dietary changes.</p>
                </div>
              </motion.div>
            )}
          </>
        ) : (
          // ====== TAB 2: Supplement Doses ======
          <>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-border p-5">
              <h2 className="font-semibold text-base mb-1">Personalized Supplement Doses</h2>
              <p className="text-xs text-muted-foreground mb-4">Doses scaled to your bodyweight. Always check with your doctor for chronic conditions.</p>

              <div className="flex gap-2">
                <input type="number" value={units === 'us' ? weightLbs : weightKg} onChange={(e) => units === 'us' ? setWeightLbs(e.target.value) : setWeightKg(e.target.value)} className="flex-1 px-3 py-2 border border-border rounded-lg" data-testid="dose-weight-input" />
                <button onClick={() => setUnits(units === 'us' ? 'metric' : 'us')} className="px-3 py-2 bg-muted rounded-lg text-xs font-medium" data-testid="dose-units-toggle">
                  {units === 'us' ? 'lbs' : 'kg'} ⇄
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">≈ {wKg} kg</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
              {DOSE_SUPPLEMENTS.map(s => (
                <div key={s.name} className="bg-white rounded-xl border border-border p-4 flex items-center justify-between" data-testid={`dose-row-${s.name.replace(/\s+/g, '-')}`}>
                  <div className="flex-1 min-w-0 pr-3">
                    <p className="font-semibold text-sm">{s.name}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{s.note}</p>
                  </div>
                  <p className="text-lg font-bold text-primary text-right flex-shrink-0">{wKg > 0 ? s.formula(wKg) : '—'}</p>
                </div>
              ))}
            </motion.div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[11px] text-amber-900 flex gap-2">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <p>These are general guidelines for healthy adults. Therapeutic doses for specific conditions may differ. Speak with a healthcare provider, especially if pregnant, on medications, or managing a chronic condition.</p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
